<template>
  <v-card
    class="uptime-card"
    elevation="1"
    rounded="lg"
    :loading="loading"
  >
    <!-- Header -->
    <v-card-title class="card-header">
      <div class="header-left">
        <div class="chart-title">
          Uptime Performance Trend
        </div>
        <div class="chart-subtitle">
          Quarterly daily-aggregation view
        </div>
      </div>
      <div class="legend">
        <span class="legend-dot" />
        <span class="legend-label">Network Uptime</span>
      </div>
    </v-card-title>

    <!-- Chart -->
    <v-card-text class="chart-area pa-0 pr-4 pb-4">
      <apexchart
        type="bar"
        height="260"
        :options="chartOptions"
        :series="series"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
export default {
  name: "PerformanceTrend",
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    loading: {
      type: Boolean,
      default() {
        return false;
      },
    },
    items: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  computed: {
    // [{ date: "2026-07-10", network_availability_percent: 0.94, ... }]
    series() {
      return [
        {
          name: "Network Uptime",
          data: this.items.map((item) => item.network_availability_percent),
        },
      ];
    },

    categories() {
      return this.items.map((item) => this.formatDate(item.date));
    },

    // Show ~4 ticks: first, last, and month boundaries in between
    tickLabels() {
      const total = this.categories.length;
      if (!total) return [];

      return this.categories.map((label, index) => {
        const isFirst = index === 0;
        const isLast = index === total - 1;
        const isMonthStart = label.endsWith("01");
        return isFirst || isLast || isMonthStart ? label : "";
      });
    },

    chartOptions() {
      return {
        chart: {
          type: "bar",
          toolbar: { show: false },
          zoom: { enabled: false },
          background: "transparent",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 600,
          },
        },
        plotOptions: {
          bar: {
            columnWidth: "75%",
            borderRadius: 3,
            borderRadiusApplication: "end",
          },
        },
        colors: ["#20a390"],
        dataLabels: { enabled: false },
        grid: {
          show: false,
        },
        xaxis: {
          categories: this.tickLabels,
          labels: {
            rotate: 0,
            style: {
              fontFamily: "Sora, sans-serif",
              fontSize: "11px",
              colors: "#20a390",
            },
            formatter: (val) => val,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false },
        },
        yaxis: {
          show: false,
          min: 0,
          max: 100,
        },
        tooltip: {
          theme: "light",
          style: {
            fontFamily: "Sora, sans-serif",
            fontSize: "12px",
          },
          x: {
            // Show the real date in tooltip even when tick label is blank
            formatter: (val, { dataPointIndex }) =>
              this.categories[dataPointIndex] || val,
          },
          y: {
            formatter: (val) => `${val}%`,
          },
        },
        states: {
          hover: {
            filter: {
              type: "none",
            },
          },
          active: {
            filter: {
              type: "none",
            },
          },
        },
      };
    },
  },

  methods: {
    // 0.94 -> 94
    toPercent(value) {
      return Math.round((value || 0) * 100 * 100) / 100;
    },

    // "2026-07-10" -> "Jul 10"
    formatDate(dateStr) {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.uptime-card {
  background: #ffffff !important;
  border: 1px solid #e8ecf0 !important;
  padding: 0px 4px 0 4px;
  height: 100%;
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px 8px 24px !important;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.chart-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a202c;
  letter-spacing: -0.01em;
  line-height: 1.3;
}

.chart-subtitle {
  font-size: 11px;
  font-weight: 400;
  color: #9aa5b4;
  letter-spacing: 0.01em;
}

.legend {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 2px;
}

.legend-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #1a202c;
  flex-shrink: 0;
}

.legend-label {
  font-size: 12px;
  font-weight: 500;
  color: #4a5568;
}

.chart-area {
  padding-left: 12px !important;
}
</style>
